import { useEffect, useMemo, useState } from "react";
import KpiCard from "./KpiCard";
import LineChartCard from "./LineChartCard";
import BarChartCard from "./BarChartCard";
import { useApiClient } from "../../service/API";

function toISODate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function DashboardBO() {
  const apiClient = useApiClient();

  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => toISODate(today), [today]);
  const defaultEnd = useMemo(() => toISODate(today), [today]);

  // DRAFT inputs (user typing/selecting)
  const [draftStart, setDraftStart] = useState(defaultStart);
  const [draftEnd, setDraftEnd] = useState(defaultEnd);
  const [draftFilterEmail, setDraftFilterEmail] = useState("");

  // APPLIED inputs (used for API)
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);
  const [filterEmail, setFilterEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const [boUsers, setBoUsers] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState("");

  const isAllUsers = !filterEmail;

  async function fetchBoUsersWithoutLeaveCheck() {
    const res = await apiClient.get("/users/bouser-list-leave");
    return res.data;
  }

  async function fetchBoDashboard({ start, end, userEmail }) {
    const params = { start, end };
    if (userEmail) params.userEmail = userEmail;
    const res = await apiClient.get("/api/dashboard/bo", { params });
    return res.data;
  }

  async function fetchTicketDueTodayList() {
    setLoadingDownload(true);

    try {
      const res = await apiClient.get("/api/dashboard/ticket-pending-complete-list", { params: {
          startDate: start || undefined,
          endDate: end || undefined,
          userEmail: filterEmail || undefined,
        },
        responseType: 'blob', });
      const blob = res.data;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'data.xlsx';
      link.click();
      URL.revokeObjectURL(link.href);
      } catch (error) {
      // console.error("There was an error fetching the job stats", error);
    } finally {
      setLoadingDownload(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const list = await fetchBoUsersWithoutLeaveCheck();
        setBoUsers(list || []);
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load(withParams = { start, end, filterEmail }) {
    setLoading(true);
    setErr("");
    try {
      const res = await fetchBoDashboard({
        start: withParams.start,
        end: withParams.end,
        userEmail: withParams.filterEmail || null,
      });

      setData(res);

      const users = res?.users || [];

      if (withParams.filterEmail) {
        setSelectedEmail(withParams.filterEmail);
      } else {
        setSelectedEmail((prev) => {
          const stillExists = users.some((u) => u.email === prev);
          if (stillExists) return prev;
          return users?.[0]?.email || "";
        });
      }
    } catch (e) {
      setErr(e?.message || "Failed to load BO dashboard");
    } finally {
      setLoading(false);
    }
  }

  // initial load using applied inputs
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // APPLY button: copy draft -> applied, then load using the draft values
  function onApply() {
    setStart(draftStart);
    setEnd(draftEnd);
    setFilterEmail(draftFilterEmail);

    // Keep selectedEmail consistent with applied filter
    if (draftFilterEmail) {
        setSelectedEmail(draftFilterEmail);
    }

    load({ start: draftStart, end: draftEnd, filterEmail: draftFilterEmail });
  }

  const users = data?.users || [];

  const allUsersSummary = useMemo(() => {
    if (!users.length) return null;
    console.log(users);

    const toNum = (v) => {
        if (v === null || v === undefined) return 0;
        const s = String(v).trim().replace("%", ""); // handles "98%"
        const n = Number(s);
        return Number.isFinite(n) ? n : 0;
    };

    const sum = (arr) => arr.reduce((a, b) => a + b, 0);
    const round2 = (n) => (Number.isFinite(n) ? Math.round(n * 100) / 100 : 0);

    const completedTodaySum = sum(users.map((u) => toNum(u.completedToday)));
    const dueTodaySum = sum(users.map((u) => toNum(u.dueToday)));
    const errorsThisWeekSum = sum(users.map((u) => toNum(u.errorsThisWeek)));
    const stageDueTodaySum = sum(users.map((u) => toNum(u.stageDueToday)));

    const avgCompletedPerDayAvg =
        users.reduce((acc, u) => acc + toNum(u.avgCompletedPerDay), 0) / users.length;

    const accuracyPctAvg =
        users.reduce((acc, u) => acc + toNum(u.accuracyPct), 0) / users.length;

    return {
        completedToday: completedTodaySum,
        dueToday: dueTodaySum,
        errorsThisWeek: errorsThisWeekSum,
        avgCompletedPerDay: round2(avgCompletedPerDayAvg),
        accuracyPct: round2(accuracyPctAvg),
        stageDueToday: stageDueTodaySum
    };
    }, [users]);

  const selectedUser = isAllUsers
    ? allUsersSummary
    : users.find((u) => u.email === selectedEmail);

  const dailyLabels = (data?.dailyTotalCompleted || []).map((x) => x.date);
  const dailyValues = (data?.dailyTotalCompleted || []).map((x) => x.count);

  const weeklyErrLabels = (data?.weeklyErrorsTotal || []).map(
    (x) => x.periodStart
  );
  const weeklyErrValues = (data?.weeklyErrorsTotal || []).map((x) => x.count);

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div style={{ minWidth: 300 }}>
          <label className="form-label mb-1">Start</label>
          <input
            style={{ height: 40 }}
            className="form-control"
            type="date"
            value={draftStart}
            onChange={(e) => setDraftStart(e.target.value)}
          />
        </div>

        <div style={{ minWidth: 300 }}>
          <label className="form-label mb-1">End</label>
          <input
            style={{ height: 40 }}
            className="form-control"
            type="date"
            value={draftEnd}
            onChange={(e) => setDraftEnd(e.target.value)}
          />
        </div>

        <div style={{ minWidth: 300 }}>
          <label className="form-label mb-1">Filter by BO user</label>
          <select
            style={{ height: 40 }}
            className="form-select"
            value={draftFilterEmail}
            onChange={(e) => {
              const v = e.target.value;
              setDraftFilterEmail(v);
            }}
          >
            <option value="">All BO users</option>
            {boUsers.map((u) => (
              <option key={u.email} value={u.email}>
                {u.email}
              </option>
            ))}
          </select>
        </div>

        <div>
          <button className="btn btn-primary" onClick={onApply} disabled={loading}>
            {loading ? "Loading..." : "Apply"}
          </button>
        </div>

        <div>
          <button className="btn btn-primary" onClick={fetchTicketDueTodayList} disabled={loading}>
            {loadingDownload ? "Loading..." : "Export Due Today Excel"}
          </button>
        </div>
      </div>

      {err ? <div className="alert alert-danger">{err}</div> : null}

      <div className="row g-3 mb-3">

        <div className="col-10 col-md-2">
          <KpiCard
            title={isAllUsers ? "ETD/ETA Passed Tickets (Total)" : "ETD/ETA Passed Tickets"}
            value={selectedUser?.stageDueToday ?? "-"}
          />
        </div>

        <div className="col-10 col-md-2">
          <KpiCard
            title={isAllUsers ? "Due Today (Total)" : "Due Today"}
            value={selectedUser?.dueToday ?? "-"}
          />
        </div>

        <div className="col-10 col-md-2">
          <KpiCard
            title={isAllUsers ? "Completed Today (Total)" : "Completed Today"}
            value={selectedUser?.completedToday ?? "-"}
          />
        </div>

        <div className="col-10 col-md-2">
          <KpiCard
            title={isAllUsers ? "Avg completed/day (Avg)" : "Avg completed per day"}
            value={selectedUser?.avgCompletedPerDay ?? "-"}
          />
        </div>

        <div className="col-10 col-md-2">
          <KpiCard
            title="Accuracy %"
            value={selectedUser ? `${selectedUser.accuracyPct}%` : "-"}
          />
        </div>

        <div className="col-10 col-md-2">
          <KpiCard
            title={isAllUsers ? "Errors this week (Total)" : "Errors this week"}
            value={selectedUser?.errorsThisWeek ?? "-"}
          />
        </div>
        
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-lg-7">
          <LineChartCard title="Daily completed" labels={dailyLabels} data={dailyValues} />
        </div>
        <div className="col-12 col-lg-5">
          <BarChartCard title="Weekly errors" labels={weeklyErrLabels} data={weeklyErrValues} />
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="fw-semibold mb-2">BO Users Summary</div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Email</th>
                  <th className="text-end">Completed Today</th>
                  <th className="text-end">Avg/Day</th>
                  <th className="text-end">Accuracy %</th>
                  <th className="text-end">Errors (This Week)</th>
                  <th className="text-end">Due Today</th>
                  <th className="text-end">Stage Due Today</th>
                </tr>
              </thead>
              <tbody>
                {users.length ? (
                  users.map((u) => (
                    <tr
                      key={u.email}
                      style={{ cursor: isAllUsers ? "default" : "pointer" }}
                      onClick={() => {
                        if (!isAllUsers) setSelectedEmail(u.email);
                      }}
                    >
                      <td className={!isAllUsers && u.email === selectedEmail ? "fw-bold" : ""}>
                        {u.email}
                      </td>
                      <td className="text-end">{u.completedToday}</td>
                      <td className="text-end">{u.avgCompletedPerDay}</td>
                      <td className="text-end">{u.accuracyPct}%</td>
                      <td className="text-end">{u.errorsThisWeek}</td>
                      <td className="text-end">{u.dueToday}</td>
                      <td className="text-end">{u.stageDueToday}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-muted">
                      No data for the selected range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
